import { memo, useMemo, useState, type FocusEvent } from 'react';
import {
  Checkbox,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
} from '@carbon/react';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import {
  COLORMAPS,
  colorBins,
  colormapStops,
  getColormap,
  type ColorMapping,
  type ColormapId,
} from 'components/colormaps';
import {
  orderedBounds,
  valueRangeLabel,
} from '../filter-modal/value-histogram';
import { BoundInput } from '../filter-modal/bound-input';
import {
  MAX_BIN_COUNT,
  MIN_BIN_COUNT,
  type SubsurfaceColorRule,
} from './color-rule';
import { RampEditorPopover, sameStops } from './ramp-editor-popover';
import styles from './color-tab.module.scss';

export const SUBSURFACE_COLOR_GROUP_ID = 'subsurfaceMaps';

const MAX_TRANSPARENCY = 90;

interface SubsurfaceColorFormProps {
  draft: SubsurfaceColorRule;
  units: string;
  transparency: number;
  onTransparencyChange: (value: number) => void;
  onChange: (patch: Partial<SubsurfaceColorRule>) => void;
}

/**
 * The tab's form: colour mapping, colormap, transparency, bins, the editable
 * preview with Reverse, and the value range (each map's own, or one global range).
 * @param {SubsurfaceColorFormProps} props The draft rule and its handlers.
 * @return {JSX.Element} The form.
 */
const SubsurfaceColorFormImpl = ({
  draft,
  units,
  transparency,
  onTransparencyChange,
  onChange,
}: SubsurfaceColorFormProps) => {
  const global = !draft.perSurfaceRange;
  const binned = draft.mapping === 'binned';
  const namedStops = useMemo(
    () => colormapStops(draft.rampId, draft.reversed),
    [draft.rampId, draft.reversed]
  );
  // ruleStops: custom stops win over the named ramp.
  const stops = draft.customStops ?? namedStops;
  const [low, high] = orderedBounds(draft.rangeMin, draft.rangeMax);
  const bins = useMemo(
    () => colorBins(low, high, stops, draft.binCount),
    [low, high, stops, draft.binCount]
  );
  // Carbon's NumberInput keeps its own '' after a clear and only re-syncs when its
  // value prop changes, so a field left empty is remounted on blur to show the value
  // still applied, as BoundInput snaps back.
  const [resyncKey, setResyncKey] = useState(0);
  const snapBackIfEmpty = (event: FocusEvent<HTMLInputElement>) => {
    if (event.target.value === '') setResyncKey((key) => key + 1);
  };

  return (
    <div className={styles.body}>
      <div className={styles.attribute}>
        <span className={styles.attributeLabel}>Attribute</span>
        <span className={styles.attributeValue}>Default Values</span>
      </div>

      <RadioButtonGroup
        legendText="Color mapping"
        name="subsurface-color-mapping"
        valueSelected={draft.mapping}
        onChange={(value) => onChange({ mapping: value as ColorMapping })}
      >
        <RadioButton
          id="subsurface-mapping-gradient"
          labelText="Gradient"
          value="gradient"
        />
        <RadioButton
          id="subsurface-mapping-binned"
          labelText="Binned ranges"
          value="binned"
        />
      </RadioButtonGroup>

      <div
        className={classNames(styles.settingsRow, { [styles.binned]: binned })}
      >
        <Select
          id="subsurface-colormap"
          labelText="Colormap"
          value={draft.customStops ? 'custom' : draft.rampId}
          onChange={(event) =>
            onChange({
              rampId: event.target.value as ColormapId,
              customStops: null,
            })
          }
        >
          {draft.customStops && <SelectItem value="custom" text="Custom" />}
          {COLORMAPS.map((colormap) => (
            <SelectItem
              key={colormap.id}
              value={colormap.id}
              text={colormap.label}
            />
          ))}
        </Select>
        <NumberInput
          key={`transparency-${resyncKey}`}
          id="subsurface-transparency"
          label="Transparency (%)"
          min={0}
          max={MAX_TRANSPARENCY}
          step={5}
          hideSteppers
          // allowEmpty passes a cleared field through as '', so it keeps the last
          // value instead of staging Number('') = 0 (fully opaque).
          allowEmpty
          value={transparency}
          onBlur={snapBackIfEmpty}
          onChange={(_, { value }) => {
            if (value === '') return;
            const number = Number(value);
            if (Number.isFinite(number)) {
              onTransparencyChange(clamp(number, 0, MAX_TRANSPARENCY));
            }
          }}
        />
        {binned && (
          <NumberInput
            key={`bins-${resyncKey}`}
            id="subsurface-bin-count"
            label="Bins"
            min={MIN_BIN_COUNT}
            max={MAX_BIN_COUNT}
            step={1}
            allowEmpty
            value={draft.binCount}
            onBlur={snapBackIfEmpty}
            onChange={(_, { value }) => {
              if (value === '') return;
              const number = Math.round(Number(value));
              if (Number.isFinite(number)) {
                onChange({
                  binCount: clamp(number, MIN_BIN_COUNT, MAX_BIN_COUNT),
                });
              }
            }}
          />
        )}
      </div>

      <div className={styles.previewBlock}>
        <span className={styles.fieldLabel}>Preview</span>
        <div className={styles.previewRow}>
          <RampEditorPopover
            title="Colour ramp"
            stops={stops}
            baseStops={namedStops}
            baseLabel={getColormap(draft.rampId).label}
            onChange={(nextStops) =>
              onChange({
                customStops: sameStops(nextStops, namedStops)
                  ? null
                  : nextStops,
              })
            }
          />
          <Checkbox
            id="subsurface-colormap-reverse"
            labelText="Reverse"
            checked={draft.reversed}
            disabled={!!draft.customStops}
            onChange={(_, { checked }) => onChange({ reversed: checked })}
          />
        </div>
      </div>

      <RadioButtonGroup
        legendText="Value range"
        name="subsurface-value-range"
        orientation="vertical"
        valueSelected={draft.perSurfaceRange ? 'map' : 'global'}
        onChange={(value) => onChange({ perSurfaceRange: value === 'map' })}
      >
        <RadioButton
          id="subsurface-range-map"
          labelText="Map Min/Max"
          value="map"
        />
        <RadioButton
          id="subsurface-range-global"
          labelText="Global Min/Max"
          value="global"
        />
      </RadioButtonGroup>

      <div className={styles.rangeRow}>
        <BoundInput
          id="subsurface-range-min"
          label={`Min (${units})`}
          disabled={!global}
          invalid={global && draft.rangeMin > draft.rangeMax}
          staged={draft.rangeMin}
          fullValue={draft.rangeMin}
          // Commits only complete numbers (so a negative can be typed, and a
          // cleared field snaps back instead of becoming 0).
          onCommit={(value) => {
            if (value !== null) onChange({ rangeMin: value });
          }}
        />
        <BoundInput
          id="subsurface-range-max"
          label={`Max (${units})`}
          disabled={!global}
          invalid={global && draft.rangeMin > draft.rangeMax}
          staged={draft.rangeMax}
          fullValue={draft.rangeMax}
          onCommit={(value) => {
            if (value !== null) onChange({ rangeMax: value });
          }}
        />
      </div>

      {binned && global && (
        <ul className={styles.binList} aria-label="Binned ranges">
          {bins.map((bin, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- index, not x0: a flat range gives every bin the same bounds.
            <li key={index} className={styles.binRow}>
              <span
                className={styles.binSwatch}
                // Bin colours are data, so they can only be painted inline.
                style={{ background: bin.color }}
                aria-hidden
              />
              <span>{valueRangeLabel(bin.x0, bin.x1, units)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SubsurfaceColorForm = memo(SubsurfaceColorFormImpl);
